import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiUploadCloud, FiCheckCircle } from 'react-icons/fi';
import ReactSelect from 'react-select';
import axios from 'axios';
import ErpTemplate from '../../components/ErpTemplate';
import api from '../../services/api';
import './UploadNFRetroativa.css';
import { Redirect } from 'react-router-dom';

export default function UploadNFRetroativa() {
    const token = localStorage.getItem('token');
    const perfil = localStorage.getItem('perfil');
    const acessoNegado = ['rsvp', 'operadorrsvp', 'listarsvp'].includes(perfil);
    
    const [eventos, setEventos] = useState([]);
    const [orcamentos, setOrcamentos] = useState([]);
    const [fornecedores, setFornecedores] = useState([]);
    
    const [eventoSelecionado, setEventoSelecionado] = useState(null);
    const [orcamentoSelecionado, setOrcamentoSelecionado] = useState(null);
    const [fornecedorSelecionado, setFornecedorSelecionado] = useState(null);
    
    const [arquivos, setArquivos] = useState([]);
    const [enviando, setEnviando] = useState(false);
    const [sucesso, setSucesso] = useState(false);
    const [quantidadeEnviada, setQuantidadeEnviada] = useState(0);
    const [quantidadeFalhas, setQuantidadeFalhas] = useState(0);
    const [dragOver, setDragOver] = useState(false);
    const [carregando, setCarregando] = useState(false);

    useEffect(() => {
        if (acessoNegado) {
            return;
        }
        carregarEventos();
    }, [acessoNegado]);

    useEffect(() => {
        if (acessoNegado) {
            return;
        }
        if (eventoSelecionado) {
            carregarOrcamentos(eventoSelecionado.value);
            setOrcamentoSelecionado(null);
            setFornecedorSelecionado(null);
        }
    }, [acessoNegado, eventoSelecionado]);

    useEffect(() => {
        if (acessoNegado) {
            return;
        }
        if (orcamentoSelecionado) {
            carregarFornecedores(orcamentoSelecionado.value);
            setFornecedorSelecionado(null);
        }
    }, [acessoNegado, orcamentoSelecionado]);

    const carregarEventos = async () => {
        try {
            setCarregando(true);
            const response = await api({
                method: 'get',
                action: '/admin/nf-retroativa/eventos',
                token: token
            });
            
            if (response.data.sucesso) {
                const eventosFormatados = response.data.eventos.map(e => ({
                    value: e.id,
                    label: `${e.nome} - ${new Date(e.data).toLocaleDateString('pt-BR')}`,
                    id: e.id,
                    nome: e.nome,
                    data: e.data
                }));
                setEventos(eventosFormatados);
            }
        } catch (error) {
            console.error('Erro ao carregar eventos:', error);
            toast.error('Erro ao carregar eventos');
        } finally {
            setCarregando(false);
        }
    };

    const carregarOrcamentos = async (eventoId) => {
        try {
            const response = await api({
                method: 'get',
                action: `/admin/nf-retroativa/orcamentos/${eventoId}`,
                token: token
            });
            
            if (response.data.sucesso) {
                const orcamentosFormatados = response.data.orcamentos.map(o => ({
                    value: o.id,
                    label: `Orçamento ${o.numero} - R$ ${o.valorTotal?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}`,
                    id: o.id,
                    numero: o.numero
                }));
                setOrcamentos(orcamentosFormatados);
            }
        } catch (error) {
            console.error('Erro ao carregar orçamentos:', error);
            toast.error('Erro ao carregar orçamentos');
        }
    };

    const carregarFornecedores = async (orcamentoId) => {
        try {
            const response = await api({
                method: 'get',
                action: `/admin/nf-retroativa/fornecedores/${orcamentoId}`,
                token: token
            });
            
            if (response.data.sucesso) {
                const fornecedoresFormatados = response.data.fornecedores.map(f => ({
                    value: f.id,
                    label: `${f.nome}${f.cnpj ? ' - ' + f.cnpj : ''}`,
                    id: f.id,
                    nome: f.nome
                }));
                setFornecedores(fornecedoresFormatados);
            }
        } catch (error) {
            console.error('Erro ao carregar fornecedores:', error);
            toast.error('Erro ao carregar fornecedores');
        }
    };

    const validarArquivo = (file) => {
        if (!file) return 'Arquivo inválido';

        const tiposPermitidos = ['application/pdf', 'image/jpeg', 'image/png'];
        if (!tiposPermitidos.includes(file.type)) {
            return 'Formato não permitido. Envie PDF, JPG ou PNG';
        }

        const tamanhoMax = 10 * 1024 * 1024; // 10MB
        if (file.size > tamanhoMax) {
            return 'Arquivo muito grande. Tamanho máximo: 10MB';
        }

        return null;
    };

    const adicionarArquivos = (novosArquivos = []) => {
        if (!novosArquivos.length) return;

        const arquivosValidos = [];

        novosArquivos.forEach((file) => {
            const erro = validarArquivo(file);
            if (erro) {
                toast.error(`${file.name}: ${erro}`);
                return;
            }

            const duplicado = arquivos.some(
                (arquivoAtual) =>
                    arquivoAtual.name === file.name &&
                    arquivoAtual.size === file.size &&
                    arquivoAtual.lastModified === file.lastModified
            ) || arquivosValidos.some(
                (arquivoNovo) =>
                    arquivoNovo.name === file.name &&
                    arquivoNovo.size === file.size &&
                    arquivoNovo.lastModified === file.lastModified
            );

            if (duplicado) {
                toast.warning(`${file.name}: arquivo já selecionado`);
                return;
            }

            arquivosValidos.push(file);
        });

        if (arquivosValidos.length > 0) {
            setArquivos((prevArquivos) => [...prevArquivos, ...arquivosValidos]);
        }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        adicionarArquivos(files);
        e.target.value = '';
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const files = Array.from(e.dataTransfer.files || []);
        adicionarArquivos(files);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    const removerArquivo = (indexRemover) => {
        setArquivos((prevArquivos) =>
            prevArquivos.filter((_, index) => index !== indexRemover)
        );
    };

    const truncateFileName = (fileName, maxLength = 50) => {
        if (fileName.length <= maxLength) return fileName;
        
        const extension = fileName.split('.').pop();
        const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
        const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 8);
        
        return `${truncatedName}....${extension}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!eventoSelecionado || !orcamentoSelecionado || !fornecedorSelecionado || arquivos.length === 0) {
            toast.error('Preencha todos os campos');
            return;
        }

        try {
            setEnviando(true);

            const formData = new FormData();
            formData.append('evento_id', eventoSelecionado.value);
            formData.append('orcamento_id', orcamentoSelecionado.value);
            formData.append('fornecedor_id', fornecedorSelecionado.value);
            arquivos.forEach((arquivo) => {
                formData.append('arquivo_nf[]', arquivo);
            });

            const response = await axios({
                method: 'post',
                url: process.env.REACT_APP_API_URL + '/admin/nf-retroativa/upload',
                data: formData,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.sucesso) {
                setQuantidadeEnviada(response.data.quantidade_enviada || arquivos.length);
                setQuantidadeFalhas(response.data.quantidade_falhas || 0);
                setSucesso(true);
                if ((response.data.quantidade_falhas || 0) > 0) {
                    toast.warning(response.data.mensagem);
                } else {
                    toast.success(response.data.mensagem || 'Nota fiscal enviada com sucesso!');
                }
            }
        } catch (error) {
            console.error('Erro ao enviar:', error);
            toast.error(error.response?.data?.mensagem || 'Erro ao enviar nota fiscal');
        } finally {
            setEnviando(false);
        }
    };

    const resetForm = () => {
        setEventoSelecionado(null);
        setOrcamentoSelecionado(null);
        setFornecedorSelecionado(null);
        setArquivos([]);
        setQuantidadeEnviada(0);
        setQuantidadeFalhas(0);
        setSucesso(false);
    };

    if (acessoNegado) {
        return <Redirect to="/evento" />;
    }

    return (
        <ErpTemplate>
            <div className="main-content container-fluid nf-retroativa-page">
                <div className="page-title">
                    <h3>Upload de NF Retroativa</h3>
                    <p className="text-muted mb-0">Envie notas fiscais de eventos já finalizados</p>
                </div>

                <section className="section">
                    <div className="row justify-content-center">
                        <div className="col-12 col-xl-10">
                            <div className="card nf-retroativa-card">
                                <div className="card-content">
                                    <div className="card-body">
                                        {sucesso ? (
                                            <div className="success-content">
                                                <FiCheckCircle size={56} color="#198754" />
                                                <h4 className="mt-3 mb-2">
                                                    {quantidadeEnviada > 1 ? 'Notas fiscais enviadas com sucesso' : 'Nota fiscal enviada com sucesso'}
                                                </h4>
                                                <p className="text-muted mb-4">
                                                    {quantidadeEnviada > 1
                                                        ? `${quantidadeEnviada} notas fiscais retroativas foram registradas no sistema.`
                                                        : 'A nota fiscal retroativa foi registrada no sistema.'}
                                                    {quantidadeFalhas > 0 ? ` ${quantidadeFalhas} arquivo(s) não foram processados.` : ''}
                                                </p>
                                                <button type="button" className="btn btn-primary" onClick={resetForm}>
                                                    Enviar novas notas
                                                </button>
                                            </div>
                                        ) : (
                                            <form className="nf-retroativa-form" onSubmit={handleSubmit}>
                                                <div className="form-group mb-3">
                                                    <label className="form-label-retro">Evento *</label>
                                                    <ReactSelect
                                                        value={eventoSelecionado}
                                                        onChange={setEventoSelecionado}
                                                        options={eventos}
                                                        placeholder="Selecione ou busque o evento..."
                                                        isLoading={carregando}
                                                        isDisabled={carregando}
                                                        noOptionsMessage={() => "Nenhum evento encontrado"}
                                                        loadingMessage={() => "Carregando eventos..."}
                                                        isClearable
                                                        className="react-select-container"
                                                        classNamePrefix="nf-react-select"
                                                    />
                                                </div>

                                                <div className="form-group mb-3">
                                                    <label className="form-label-retro">Orçamento *</label>
                                                    <ReactSelect
                                                        value={orcamentoSelecionado}
                                                        onChange={setOrcamentoSelecionado}
                                                        options={orcamentos}
                                                        placeholder="Selecione o orçamento..."
                                                        isDisabled={!eventoSelecionado || orcamentos.length === 0}
                                                        noOptionsMessage={() => "Nenhum orçamento encontrado"}
                                                        isClearable
                                                        className="react-select-container"
                                                        classNamePrefix="nf-react-select"
                                                    />
                                                </div>

                                                <div className="form-group mb-3">
                                                    <label className="form-label-retro">Fornecedor *</label>
                                                    <ReactSelect
                                                        value={fornecedorSelecionado}
                                                        onChange={setFornecedorSelecionado}
                                                        options={fornecedores}
                                                        placeholder="Selecione o fornecedor..."
                                                        isDisabled={!orcamentoSelecionado || fornecedores.length === 0}
                                                        noOptionsMessage={() => "Nenhum fornecedor encontrado"}
                                                        isClearable
                                                        className="react-select-container"
                                                        classNamePrefix="nf-react-select"
                                                    />
                                                </div>

                                                <div className="form-group mb-4">
                                                    <label className="form-label-retro">Arquivos das Notas Fiscais *</label>
                                                    <div
                                                        className={`dropzone-retro ${dragOver ? 'dragover' : ''} ${arquivos.length > 0 ? 'has-file' : ''}`}
                                                        onDrop={handleDrop}
                                                        onDragOver={handleDragOver}
                                                        onDragLeave={handleDragLeave}
                                                        onClick={() => document.getElementById('fileInput').click()}
                                                    >
                                                        <input
                                                            id="fileInput"
                                                            type="file"
                                                            accept=".pdf,.jpg,.jpeg,.png"
                                                            multiple
                                                            onChange={handleFileChange}
                                                            style={{ display: 'none' }}
                                                        />
                                                        {arquivos.length === 0 ? (
                                                            <>
                                                                <FiUploadCloud size={50} />
                                                                <p>Clique ou arraste os arquivos aqui</p>
                                                                <span className="file-types">PDF, JPG ou PNG (máx. 10MB por arquivo)</span>
                                                            </>
                                                        ) : (
                                                            <div className="files-selected-retro">
                                                                <FiCheckCircle size={40} color="#198754" />
                                                                <p className="files-count-retro">
                                                                    {arquivos.length} arquivo(s) selecionado(s)
                                                                </p>
                                                                <div className="files-list-retro">
                                                                    {arquivos.map((arquivo, index) => (
                                                                        <div className="file-item-retro" key={`${arquivo.name}-${arquivo.lastModified}-${index}`}>
                                                                            <span className="file-name-retro">{truncateFileName(arquivo.name, 60)}</span>
                                                                            <span className="file-size-retro">
                                                                                {(arquivo.size / 1024 / 1024).toFixed(2)} MB
                                                                            </span>
                                                                            <button
                                                                                type="button"
                                                                                className="btn-remove-retro"
                                                                                onClick={(event) => {
                                                                                    event.stopPropagation();
                                                                                    removerArquivo(index);
                                                                                }}
                                                                            >
                                                                                Remover
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <button
                                                    type="submit"
                                                    className="btn btn-primary"
                                                    disabled={!eventoSelecionado || !orcamentoSelecionado || !fornecedorSelecionado || arquivos.length === 0 || enviando}
                                                >
                                                    {enviando ? 'Enviando...' : `Enviar ${arquivos.length > 1 ? `${arquivos.length} notas fiscais` : 'nota fiscal'}`}
                                                </button>
                                            </form>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </ErpTemplate>
    );
}
